import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BaseContext } from 'context/base.context';
import { RoutePath } from 'routes/route-path.constant';

import { useSaveAnswers } from 'hooks/use-save-answers.hook';
import { useLocalStorage } from 'hooks/use-local-storage.hook';

import { LocalStorageKeys } from 'constants/local-storage';
import { TypographyVariants } from 'constants/typography-variants';

import { Typography } from 'components/typography';
import { NextButton } from 'components/next-button';
import { MultiChoiceButton, MultiChoiceType } from 'components/multichoice-button';
import { FloatedButton } from 'components/floated-button';
import { getMultiChoiceButtonSelectOptions } from 'helpers/get-multichoice-button-select-options';

export const Motivation = () => {
    const { t } = useTranslation();
    const saveAnswers = useSaveAnswers();

    const { pageConfiguration } = useContext(BaseContext);

    const pageKey = LocalStorageKeys[RoutePath.Motivation];

    const { pageValue, setPageValue } = useLocalStorage({
        key: pageKey,
        defaultValue: [],
    });

    const { pageValue: userAnswer, setPageValue: setUserAnswer } = useLocalStorage({
        key: `${pageKey}-other-userAnswer`,
        defaultValue: '',
    });

    const multiChoices: MultiChoiceType[] = [
        {
            key: 'health',
            value: t('motivation-option-health')
        },
        {
            key: 'look-better',
            value: t('motivation-option-look-better')
        },
        {
            key: 'feel-more-confident',
            value: t('motivation-option-feel-more-confident')
        },
        {
            key: 'get-stronger',
            value: t('motivation-option-get-stronger')
        },
        {
            key: 'increase-energy',
            value: t('motivation-option-increase-energy')
        },
        {
            key: 'recover',
            value: t('motivation-option-recover')
        },
        {
            key: 'improve-sports-performance',
            value: t('motivation-option-improve-sports-performance')
        },
        {
            key: 'other',
            value: t('motivation-option-other'),
            userAnswer,
            setUserAnswer
        },
    ];

    const handleClick = () => {
        const selectOption = getMultiChoiceButtonSelectOptions(pageValue, multiChoices);

        saveAnswers({
            key: pageKey,
            itemQuestion: { question: t('motivation-title'), value: selectOption },
        });

        pageConfiguration.handleOnPageComplete({
            value: pageValue,
            userAnswer: pageValue.includes('other') ? userAnswer.trim() : ''
        });
    };

    return (
        <>
            <Typography variant={TypographyVariants.h1} typographyNext>
                <Trans i18nKey='motivation-title' />
            </Typography>

            <Typography variant={TypographyVariants.h2}>
                <Trans i18nKey='motivation-subtitle' />
            </Typography>

            {multiChoices.map((v: MultiChoiceType) => (
                <MultiChoiceButton
                    key={v.key}
                    item={v}
                    pageValue={pageValue}
                    setPageValue={setPageValue}
                />
            ))}

            <FloatedButton floated={pageValue.length} withGradient>
                <NextButton onClick={handleClick} disabled={!pageValue.length} />
            </FloatedButton>
        </>
    );
};
